/**
 * Application self-update contract (见 25).
 *
 * The one thing this file exists to make true: **「有更新」这件事在每个运行端是同一个
 * 状态机、同一套动词、同一个弹窗**，各端只在「怎么拿到新版本」和「多久问一次」上不同。
 * 桌面下载安装包并重启，浏览器刷新一次页面——那是 driver 的事，不是消费方的事。
 *
 * Shaped like `attention-contract.ts`: pure types plus pure functions, no
 * environment API anywhere, so the extension host (which owns the state) and both
 * runtimes' drivers and the renderer all import the same file.
 *
 * Two things are deliberately *not* here:
 *
 * - **The driver interface.** It is declared next to its consumer
 *   (`update-service.ts` in the extension host), the same way `AttentionDriver` is
 *   — the wire between host and main process is a message, not a shared type.
 * - **Any notion of an installer format.** Whether the desktop uses NSIS, an
 *   archive or a package manager never reaches this contract; the only thing the
 *   UI is allowed to ask is {@link UpdateCapabilities.canSelfInstall}.
 */
import { parseVersion } from './semver';

/** RPC channel the extension host serves {@link IUpdateService} on. */
export const UPDATE_CHANNEL = 'update';

/**
 * EventBus event fired whenever {@link UpdateState} changes.
 *
 * Not in `PLATFORM_PUBLIC_EVENTS`: updating the application is the shell's
 * business, and a sandboxed surface has no use for the release notes of a build
 * it cannot install.
 */
export const UPDATE_CHANGED_EVENT = 'platform.update.changed';

/**
 * How often a background check runs when nothing else triggers one.
 *
 * Four hours rather than something tighter because the cost of learning about a
 * release late is a few hours of running last week's build, while the cost of
 * asking often is a request from every installed client against a static file.
 */
export const DEFAULT_UPDATE_CHECK_INTERVAL_MINUTES = 240;

/** A release the current build could move to. */
export interface UpdateInfo {
  /** Version of the offered release, e.g. `1.4.2`. */
  readonly version: string;
  /** Release notes, shown in the prompt when present. */
  readonly notes?: string;
  /** ISO timestamp of publication, for the prompt's secondary line. */
  readonly publishedAt?: string;
  /**
   * Builds older than this must update before the workbench is usable (见 25
   * 强制更新). Absent means the update is optional, which is the normal case —
   * this exists for the release that breaks a server contract.
   */
  readonly minVersion?: string;
  /**
   * The **source already decided** this update is compulsory, so no local
   * comparison is needed (see {@link isMandatoryUpdate}).
   *
   * Set from `force` on the platform service's `app_update` payload, which the
   * service computed against its own `force_version`. Kept separate from
   * {@link minVersion} rather than back-filled into it because the two answer
   * different questions: `minVersion` is a floor we compare the running build
   * against, this is a verdict already reached. Recomputing a verdict from a
   * floor we were not given is how two implementations of "how old is too old"
   * start disagreeing.
   */
  readonly mandatory?: boolean;
  /**
   * Where a human can fetch the package themselves. Required reading only when
   * {@link UpdateCapabilities.canSelfInstall} is false, where it is the *only*
   * way forward and the prompt says so instead of offering an install button.
   */
  readonly downloadUrl?: string;
  /**
   * Windows already tried to apply this exact package on launch and stopped
   * (见 25). The file is still on disk; the next launch will not start the
   * installer again until the user asks. Asking (`install()`) clears the
   * attempt count and quits, so the following start tries once.
   */
  readonly automaticInstallExhausted?: boolean;
}

/**
 * What the current runtime can actually do about an update.
 *
 * This is a **capability declaration, not a fallback**: a runtime that cannot
 * install says so up front, and the UI offers a download link rather than a
 * button that would fail. The same rule the settings panel already follows with
 * `BundleInstallBridge.canInstall` — "not offered" beats "offered and broken".
 *
 * The honest case today is Linux: deb/rpm updates go through the distribution's
 * package manager and need elevation, so that runtime only checks and points at
 * a download page.
 */
export interface UpdateCapabilities {
  /** Whether asking "is there a newer build" is possible at all. */
  readonly canCheck: boolean;
  /**
   * Whether applying an update has a **separate fetch step** before the swap.
   *
   * True on the desktop, where an installer has to come down the wire first.
   * False in the browser: there is no payload to pull, the new build is already
   * being served and a reload picks it up — so the prompt goes straight from
   * "available" to "install" with no progress bar in between.
   */
  readonly canDownload: boolean;
  /** Whether the runtime can complete the swap without the user running an installer. */
  readonly canSelfInstall: boolean;
  /**
   * Whether applying it **hands the package to the OS and stops there**, leaving
   * the last step to the user.
   *
   * Only meaningful when {@link canSelfInstall} is true, and it exists so the
   * prompt's primary button can say what will actually happen without asking
   * which OS it is on (`platform-capability-first`): a Windows installer
   * replaces the app and relaunches it, while opening a macOS `.dmg` mounts a
   * volume the user still has to drag out of. Both are "we can apply this
   * without sending you to a web page"; only one of them ends with the app
   * updated.
   */
  readonly installNeedsUser?: boolean;
  /**
   * Whether a downloaded update is automatically handed to its installer when
   * the app next launches.
   *
   * This is separate from {@link canSelfInstall}: a runtime can install on
   * demand without also staging itself for the next launch.
   */
  readonly installsOnLaunch?: boolean;
}

/** No driver injected: this runtime does not do updates at all. */
export const NO_UPDATE_CAPABILITIES: UpdateCapabilities = {
  canCheck: false,
  canDownload: false,
  canSelfInstall: false,
};

/**
 * Who this build is, plus what it can do about updates.
 *
 * One value rather than three calls because the settings section needs exactly
 * this triple to render a single line ("1.4.2 · beta" + the right button), and
 * splitting it would make that row assemble itself from three round trips that
 * can disagree mid-flight.
 */
export interface UpdateIdentity {
  /** The running build's version — `app.getVersion()` on the desktop. */
  readonly version: string;
  /** Release channel (见 01 渠道身份), e.g. `stable` / `beta` / `dev`. */
  readonly channel: string;
  readonly capabilities: UpdateCapabilities;
}

/**
 * The whole update lifecycle, as one value every window renders from.
 *
 * `manual` rides on `checking` and `error` because those two are the only states
 * whose *presentation* depends on who asked: a background check that fails is a
 * log line, the same failure after the user pressed a button has to be shown to
 * them or the button looks broken. Everything else reads the same either way.
 */
export type UpdateState =
  | {
      readonly status: 'idle';
      /** When the last completed check finished; absent until the first one. */
      readonly checkedAt?: number;
    }
  | {
      readonly status: 'checking';
      readonly manual: boolean;
      /**
       * A manual check is waiting for the desktop agent socket (见 25 HI-768).
       *
       * The first attempt already failed with {@link isRetryableAgentIpcError};
       * the host is backing off and will ask again. The settings row says
       * 「连接中」instead of flashing that IPC sentence. Absent on a check that
       * is still on its first try, and never set for a background check
       * (that miss lands on `error`; {@link shouldShowUpdateConnecting}
       * still paints the settings row as「连接中」).
       */
      readonly awaitingAgent?: boolean;
    }
  | {
      readonly status: 'available';
      readonly info: UpdateInfo;
      /** Derived from {@link UpdateInfo.minVersion}; see {@link isMandatoryUpdate}. */
      readonly mandatory: boolean;
      /**
       * The user chose「稍后」for this version (见 25 稍后).
       *
       * A projection, not the source: the host keeps the dismissed *version* for
       * as long as it lives, and stamps this flag onto every offer it rebuilds.
       * That is what makes「稍后」one decision per run rather than one per check
       * — the background timer keeps polling, and a flag that lived only here
       * would be wiped by the next tick.
       */
      readonly dismissed: boolean;
    }
  | {
      readonly status: 'downloading';
      readonly info: UpdateInfo;
      readonly mandatory: boolean;
      /** 0-100; drivers that cannot report progress stay at 0 (见 25). */
      readonly percent: number;
      /** Carried across from the offer: "later" outlives the fetch (见 25). */
      readonly dismissed: boolean;
    }
  | {
      readonly status: 'ready';
      readonly info: UpdateInfo;
      readonly mandatory: boolean;
      /**
       * Still dismissable **because the user may never have agreed to anything**:
       * `update.autoDownload` is on by default, so a build can travel
       * available → downloading → ready without a single click, and a prompt
       * that cannot be closed is then something nobody asked for (见 25).
       */
      readonly dismissed: boolean;
    }
  | {
      readonly status: 'error';
      readonly message: string;
      readonly at: number;
      readonly manual: boolean;
      /**
       * Which step failed (见 25).
       *
       * Carried because the three read very differently to a user and the copy
       * has to match: "检查更新失败" for a check, "下载更新失败" for a download.
       * Learned the hard way — a missing `app-update.yml` surfaces at download
       * time, and labelling it as a failed check sent the first person to debug
       * it looking at the wrong half of the system.
       */
      readonly phase: UpdatePhase;
    };

/** The step an {@link UpdateState} error happened in (见 25). */
export type UpdatePhase = 'check' | 'download' | 'install';

/** The state a host starts in: nothing known yet. */
export const INITIAL_UPDATE_STATE: UpdateState = { status: 'idle' };

/**
 * Cross-process face of the update subsystem (见 25). Lives single-instance in
 * the extension host next to the configuration and theme services, and is
 * consumed by trusted renderers through an auto RPC proxy.
 */
export interface IUpdateService {
  /** Current lifecycle state; also broadcast on {@link UPDATE_CHANGED_EVENT}. */
  getState(): Promise<UpdateState>;
  /** Version + channel + what this runtime can do (见 {@link UpdateIdentity}). */
  getIdentity(): Promise<UpdateIdentity>;
  /**
   * Ask the feed whether a newer build exists.
   *
   * `manual` is the user pressing「检查更新」: it surfaces failures, and it clears
   * a previous "later" so an explicitly requested check always shows its answer.
   */
  check(manual: boolean): Promise<void>;
  /**
   * Drop an in-flight manual check, including the agent-wait retry loop (见 25).
   *
   * The settings overlay calls this on unmount so closing the panel does not
   * leave a backoff timer asking the agent after the user has walked away.
   * Restores the state from before that press. A no-op when no manual check
   * is in flight.
   */
  cancelCheck(): Promise<void>;
  /** Start fetching the offered release (no-op unless `status === 'available'`). */
  download(): Promise<void>;
  /** Apply it: restart into the new build on desktop, reload the page on web. */
  install(): Promise<void>;
  /** "Later" — hide the prompt for this version until the next manual check. */
  dismiss(): Promise<void>;
}

/**
 * File the release feed serves alongside its artifacts, carrying the parts of a
 * release that are **policy rather than payload** (见 25 统一发布策略).
 *
 * Sits next to the `<channel>.yml` update info in the same per-channel
 * directory, so one place
 * answers "what is this channel's current policy" for every runtime.
 */
export const RELEASE_POLICY_FILENAME = 'release-policy.json';

/**
 * The release policy both runtimes read from the shared feed (见 25).
 *
 * **Deliberately not the source of "is there an update".** That question stays
 * with each runtime's own driver, and the split is load-bearing rather than
 * tidy: on the desktop the feed *is* the payload, but in a browser "installing"
 * is a reload, and what a reload actually yields is decided by the origin — not
 * by any file we host elsewhere. A feed that claimed a new build the origin was
 * not yet serving would prompt, reload, change nothing, and prompt again.
 *
 * So the feed carries only what it can be authoritative about: how old is too
 * old, and what changed.
 */
export interface ReleasePolicy {
  /** Builds below this must update (见 {@link isMandatoryUpdate}). */
  readonly minVersion?: string;
  /** Release notes, shown in the prompt when the driver supplied none. */
  readonly notes?: string;
}

/**
 * Where the policy lives for a given feed, or `undefined` when this build
 * follows no feed (`update.feedUrl` empty — 见 05).
 *
 * Tolerates a feed URL written with or without a trailing slash, because both
 * are what people type and neither is wrong.
 */
export function releasePolicyUrl(feedUrl: string): string | undefined {
  const base = feedUrl.trim();
  if (!base) {
    return undefined;
  }
  return `${base.replace(/\/+$/, '')}/${RELEASE_POLICY_FILENAME}`;
}

/**
 * Read a policy document defensively.
 *
 * Every field is optional and anything unrecognized is dropped: this file is
 * edited by hand at release time, and one typo must not be able to break the
 * update path for clients that are otherwise fine.
 */
export function parseReleasePolicy(raw: unknown): ReleasePolicy {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return {};
  }
  const source = raw as Record<string, unknown>;
  const read = (key: string): string | undefined => {
    const value = source[key];
    return typeof value === 'string' && value.trim() !== ''
      ? value.trim()
      : undefined;
  };
  const minVersion = read('minVersion');
  const notes = read('notes');
  return {
    ...(minVersion ? { minVersion } : {}),
    ...(notes ? { notes } : {}),
  };
}

/**
 * Fold a feed policy into the offer a driver produced (见 25).
 *
 * The driver wins where both have something to say: the desktop's notes come
 * out of the feed's update info file, which is generated from the very artifact
 * being offered,
 * so it is the more specific answer. `minVersion` only ever comes from the
 * policy — no driver produces one, and a value baked into the installed build
 * could not do this job anyway (the build you need to force off a machine is
 * exactly the one carrying the stale value).
 */
export function applyReleasePolicy(
  info: UpdateInfo,
  policy: ReleasePolicy,
): UpdateInfo {
  return {
    ...info,
    ...(policy.minVersion && !info.minVersion
      ? { minVersion: policy.minVersion }
      : {}),
    ...(policy.notes && !info.notes ? { notes: policy.notes } : {}),
  };
}

/**
 * Whether this update is compulsory for `currentVersion`.
 *
 * Two ways to be: the source said so ({@link UpdateInfo.mandatory}, which the
 * platform service already computed against its own floor), or the build is
 * below {@link UpdateInfo.minVersion} from the release policy. The verdict wins
 * when present — re-deriving it here from a floor we were never given could only
 * produce a second, quieter answer.
 *
 * Fail-open on anything unparseable: a malformed `minVersion` in a feed we do
 * not control must not be able to lock every client out of the workbench. The
 * update is still offered — it just does not become compulsory.
 */
export function isMandatoryUpdate(
  currentVersion: string,
  info: UpdateInfo,
): boolean {
  if (info.mandatory) {
    return true;
  }
  if (!info.minVersion) {
    return false;
  }
  const current = parseVersion(currentVersion);
  const minimum = parseVersion(info.minVersion);
  if (!current || !minimum) {
    return false;
  }
  return (
    current.major < minimum.major ||
    (current.major === minimum.major &&
      (current.minor < minimum.minor ||
        (current.minor === minimum.minor && current.patch < minimum.patch)))
  );
}

/**
 * Whether the update prompt should be on screen for this state.
 *
 * A pure rule rather than a chain of conditions inside the component so both the
 * "later" semantics and the mandatory override are testable, and so the same
 * judgement cannot drift between the prompt and anything that later wants to ask
 * the same question (the gate, a status-bar entry).
 *
 * **"Later" holds through the fetch and past it.** `downloading` and `ready`
 * used to return `true` unconditionally, on the reasoning that the user had
 * already agreed by then — which is false whenever `update.autoDownload` is on
 * (the default): the build gets there on its own, and the prompt arrived with a
 * close button and a「稍后」that both did nothing. A dismissal now survives the
 * transitions, so it is one decision per run rather than one per state.
 *
 * Only `mandatory` overrides it, in every state that carries an offer.
 */
export function shouldShowUpdatePrompt(state: UpdateState): boolean {
  switch (state.status) {
    case 'available':
    case 'downloading':
    case 'ready':
      return state.mandatory || !state.dismissed;
    default:
      return false;
  }
}

/** The states that carry an offer, i.e. the ones「稍后」can apply to (见 25). */
export type UpdateOfferState = Extract<UpdateState, { info: UpdateInfo }>;

/**
 * Whether this state carries an offer.
 *
 * A type guard rather than three inline comparisons because the set is now read
 * in several places (the prompt's gate, the dismissal, the settings row) and
 * TypeScript cannot spread a narrowed union without one — every caller would
 * otherwise re-spell the same three-member switch and they would drift.
 */
export function isUpdateOffer(state: UpdateState): state is UpdateOfferState {
  return (
    state.status === 'available' ||
    state.status === 'downloading' ||
    state.status === 'ready'
  );
}

/**
 * Whether a failed check should stay on「连接中」and ask again (见 25 HI-768).
 *
 * These two are the agent's socket not being up yet — the common race right
 * after install, when the client is running and the resident process is not.
 * `closed` is an intentional shutdown and is not in this set; a timeout or an
 * HTTP failure is a real answer and must surface.
 */
export function isRetryableAgentIpcError(message: string): boolean {
  return (
    message === 'platform-services IPC is not connected' ||
    message === 'platform-services IPC disconnected'
  );
}

/**
 * Settings-row copy for「连接中」(见 25 HI-768).
 *
 * True while a manual check is backing off on the agent socket, and also
 * when a background check already failed with {@link isRetryableAgentIpcError}:
 * the next cadence tick will ask again, so the row must not flash the IPC
 * sentence as a failed check.
 */
export function shouldShowUpdateConnecting(state: UpdateState): boolean {
  if (state.status === 'checking') {
    return state.awaitingAgent === true;
  }
  if (state.status === 'error') {
    return state.phase === 'check' && isRetryableAgentIpcError(state.message);
  }
  return false;
}

/**
 * Whether asking「检查更新」right now would do anything.
 *
 * **The settings button and the service used to decide this separately, and they
 * drifted**: the service refuses while a package is downloading (a second
 * check would only lose the in-flight fetch, and the agent rejects one that
 * overlaps a download), but the button only greyed out for `checking`. The
 * result was an enabled control that silently did nothing, which reads as a
 * broken button rather than a deliberate refusal.
 *
 * `ready` is allowed. A newer release can be published after the package was
 * staged, and the service keeps that package unless the answer is strictly
 * newer (见 25). Refusing the check here is what left 1.0.1 on screen after
 * 1.0.2 had shipped.
 *
 * A pure rule for the same reason {@link shouldShowUpdatePrompt} is one: both
 * callers now read one answer, and the disabled state is *by construction* the
 * set of cases the verb declines.
 */
export function canRequestUpdateCheck(
  state: UpdateState,
  capabilities: UpdateCapabilities,
): boolean {
  if (!capabilities.canCheck) {
    return false;
  }
  switch (state.status) {
    case 'checking':
    case 'downloading':
      return false;
    default:
      return true;
  }
}
